import { useAuth } from '@/hooks/UseAuth';
import { useLanguage } from '@/hooks/UseLanguage';
import { useRole } from '@/hooks/UseRole';
import i18n, { supportedLngs } from '@/translations/config';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

interface AuthWrapperProps {
  requiresAuth?: boolean;
  isAuthRoute?: boolean;
  allowedRoles?:
    | 'admin'
    | 'manager'
    | 'user'
    | ('admin' | 'manager' | 'user')[];
  needCheckAuth?: boolean;
  children?: ReactNode;
}

export const AuthWrapper = ({
  requiresAuth = false,
  isAuthRoute = false,
  allowedRoles,
  needCheckAuth = false,
  children,
}: AuthWrapperProps) => {
  const location = useLocation();
  const { isAuth, isCheckingAuth, isReady, user } = useAuth(needCheckAuth);
  const { initLanguage } = useLanguage();
  const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);

  const roleCheck = useRole(allowedRoles || []);

  const isAllowed = useMemo(
    () => (allowedRoles ? roleCheck : true),
    [allowedRoles, roleCheck],
  );

  // Initialize language
  useEffect(() => {
    let isMounted = true;

    const initAppLanguage = async () => {
      if (!isCheckingAuth) {
        try {
          await initLanguage();
          if (isMounted) {
            setIsLanguageInitialized(true);
          }
        } catch (error) {
          console.error('Error initializing language:', error);
        }
      }
    };

    initAppLanguage();

    return () => {
      isMounted = false;
    };
  }, [isCheckingAuth, initLanguage]);

  if (isCheckingAuth || !isReady || !isLanguageInitialized) {
    return null;
  }

  // Handle restoring the last path visited
  if (
    (isAuth &&
      localStorage.getItem('last_path') &&
      (location.pathname === `/${i18n.language}` ||
        location.pathname === `/${i18n.language}/sign-up`)) ||
    (location.pathname === `/${i18n.language}/dashboard` &&
      localStorage.getItem('last_path')?.includes('/accessory/') &&
      localStorage.getItem('from_social_login'))
  ) {
    const storedPath = localStorage.getItem('last_path') || '';
    const pathSegments = storedPath.split('/');
    const pathLang = pathSegments[1];

    if (!supportedLngs.includes(pathLang)) {
      pathSegments[1] = i18n.language;
    }

    const newPath = pathSegments.slice(1).join('/');

    return <Navigate to={`/${newPath}`} replace />;
  }

  // Redirect authenticated users away from auth routes
  if (isAuthRoute && isAuth) {
    return <Navigate to={`/${i18n.language}/dashboard`} replace />;
  }

  // Redirect unauthenticated users away from protected routes
  if (requiresAuth && !isAuth) {
    if (location.pathname.includes('/link')) {
      return (
        <Navigate to={`/${i18n.language}`} state={{ from: location }} replace />
      );
    }
    return <Navigate to={`/${i18n.language}`} replace />;
  }

  if (allowedRoles && isAuth) {
    if (!user && requiresAuth) {
      return null;
    }

    if (user && !isAllowed) {
      return <Navigate to={`/${i18n.language}/dashboard`} replace />;
    }
  }

  return <>{children || <Outlet />}</>;
};
